import { useRef, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
  IoBusinessOutline,
  IoCheckmarkCircle,
  IoChevronBack,
  IoCloseOutline,
  IoDocumentTextOutline,
  IoLocationOutline,
  IoPersonOutline,
  IoCallOutline,
  IoMailOutline,
  IoLogoApple,
  IoLogoGooglePlaystore,
} from 'react-icons/io5';
import DaumPostcode from 'react-daum-postcode';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc } from 'firebase/firestore';
import { signup } from '../../services/authService';
import { setConsentPath } from '../../services/consentService';
import { storage, db } from '../../firebase/config';
import { usePhoneOtp, formatOtpTime, OTP_LENGTH } from '../../hooks/usePhoneOtp';
import {
  ocrAndExtractBusinessNumber,
  verifyBusinessLicenseRemote,
} from '../../services/businessLicenseOcr';
import LoadingOverlay from '../../components/common/LoadingOverlay';
import EmailDomainInput from '../../components/common/EmailDomainInput';
import { getSafeSignupRedirect } from '../../utils/signupRedirect';

const TERM_ITEMS = [
  { id: 'service', label: '[필수] 서비스 이용약관', required: true },
  { id: 'privacy', label: '[필수] 개인정보 처리방침', required: true },
  { id: 'realtor_consent', label: '[필수] 공인중개사 동의서', required: true },
];

const initialTermsState = () =>
  TERM_ITEMS.reduce((acc, t) => ({ ...acc, [t.id]: false }), {});

function formatPhone(val) {
  const d = val.replace(/[^0-9]/g, '').slice(0, 11);
  if (d.length < 4) return d;
  if (d.length < 8) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
}

function formatBizNo(val) {
  const d = val.replace(/[^0-9]/g, '').slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
}

function FieldShell({ label, required, icon: Icon, children }) {
  return (
    <div>
      <label className="mb-1.5 flex items-center gap-2 text-sm font-medium text-slate-700">
        {Icon ? <Icon className="h-4 w-4 text-slate-400" /> : null}
        {label}
        {required ? <span className="text-red-500">*</span> : null}
      </label>
      {children}
    </div>
  );
}

function InputField({ label, value, onChange, placeholder, type = 'text', required, icon }) {
  return (
    <FieldShell label={label} required={required} icon={icon}>
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-300 focus:ring-4 focus:ring-blue-100"
      />
    </FieldShell>
  );
}

function PasswordField({ label, value, onChange, placeholder }) {
  return (
    <InputField
      label={label}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required
      type="password"
    />
  );
}

function SectionHeading({ title, description }) {
  return (
    <div className="space-y-1">
      <h2 className="text-base font-semibold text-slate-900">{title}</h2>
      <p className="text-sm leading-6 text-slate-500">{description}</p>
    </div>
  );
}

export default function RealtorSignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referralCode = (searchParams.get('ref') || '').trim();
  const inviteCode = (searchParams.get('invite') || '').trim();
  const signupRedirectTarget = getSafeSignupRedirect(searchParams);

  const [userType, setUserType] = useState('personal');
  const [officeName, setOfficeName] = useState('');
  const [businessNumber, setBusinessNumber] = useState('');
  const [licenseFile, setLicenseFile] = useState(null);
  const licenseInputRef = useRef(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const phoneDigits = phone.replace(/\D/g, '');
  const otp = usePhoneOtp({ phoneDigits, purpose: 'signup' });
  const otpInputsRef = useRef([]);
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [addressDetail, setAddressDetail] = useState('');
  const [addressModal, setAddressModal] = useState(false);
  const [region, setRegion] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [terms, setTerms] = useState(initialTermsState);
  const [loading, setLoading] = useState(false);
  const submittingRef = useRef(false);

  const requiredIds = TERM_ITEMS.filter((t) => t.required).map((t) => t.id);
  const allRequiredChecked = requiredIds.every((id) => terms[id]);
  const allChecked = TERM_ITEMS.every((t) => terms[t.id]);

  const toggleAll = () => {
    const next = !allChecked;
    setTerms(TERM_ITEMS.reduce((acc, t) => ({ ...acc, [t.id]: next }), {}));
  };
  const toggleTerm = (id) => setTerms((prev) => ({ ...prev, [id]: !prev[id] }));
  const [errorMsg, setErrorMsg] = useState('');

  const bizNoDigits = businessNumber.replace(/[^0-9]/g, '');
  const businessValid =
    userType !== 'business' || (bizNoDigits.length === 10 && !!licenseFile);

  const isValid =
    officeName.trim() &&
    name.trim() &&
    phone.trim() &&
    otp.verified &&
    email.trim() &&
    address.trim() &&
    password.length >= 8 &&
    password === passwordConfirm &&
    allRequiredChecked &&
    businessValid;

  const handleAddressSelect = (data) => {
    const picked = data?.roadAddress || data?.jibunAddress || data?.address || '';
    setAddress(picked);
    setAddressModal(false);
  };

  const handleLicenseChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setErrorMsg('파일 크기는 10MB 이하여야 합니다.');
      return;
    }
    setLicenseFile(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    // 더블클릭/연속 제출 동기 차단 — setLoading은 비동기라 빠른 두 번째 클릭이 통과해 중복 가입이 발생함
    if (submittingRef.current) return;
    submittingRef.current = true;

    let ocrResult = null;
    let ocrFailed = false;

    try {
      if (!isValid) return;
      if (!allRequiredChecked) {
        setErrorMsg('필수 약관에 모두 동의해 주세요.');
        return;
      }
      if (userType === 'business') {
        if (bizNoDigits.length !== 10) {
          setErrorMsg('사업자 등록번호 10자리를 입력해 주세요.');
          return;
        }
        if (!licenseFile) {
          setErrorMsg('사업자 등록증 파일을 첨부해 주세요.');
          return;
        }
      }

      setLoading(true);
      setErrorMsg('');

      // 사업자 가입: OCR 사업자번호 일치 확인
      // - BIZNO_MISMATCH: 위변조 의심 → 가입 차단
      // - NO_BIZNO_FOUND / OCR 실패: 사업자등록증 아닌 파일로 보임 → 가입은 진행, 백엔드에서 자동반려 처리
      if (userType === 'business') {
        try {
          ocrResult = await ocrAndExtractBusinessNumber(licenseFile, bizNoDigits);
        } catch (ocrErr) {
          console.error('사업자등록증 OCR 실패:', ocrErr);
          ocrFailed = true;
          ocrResult = { ok: false, reason: 'NO_BIZNO_FOUND' };
        }
        if (!ocrResult.ok) {
          if (ocrResult.reason === 'BIZNO_MISMATCH') {
            setErrorMsg('입력하신 사업자번호와 사업자등록증의 사업자번호가 일치하지 않습니다.');
            return;
          }
          ocrFailed = true;
        }
        // 국세청 사전 검증은 가입 차단 안 함 — 만료/폐업이어도 가입 진행, 로그인 시점에 차단
      }

      const { uid } = await signup({
        email: email.trim(),
        password,
        name: name.trim(),
        phone: phone.replace(/-/g, ''),
        businessName: officeName.trim(),
        role: 'realtor',
        verifiedToken: otp.verifiedToken,
        agreedTermKeys: TERM_ITEMS.filter((t) => terms[t.id]).map((t) => t.id),
        profile: {
          user_type: userType,
          business_number: userType === 'business' ? businessNumber.trim() : '',
          businessNumber: userType === 'business' ? businessNumber.trim() : '',
          address: address.trim(),
          address_detail: addressDetail.trim(),
          region: region.trim() || '',
          referrer_id: referralCode || '',
          invite_code: inviteCode || '',
          consent: true,
          terms_agreed: terms,
        },
      });

      if (userType === 'business' && licenseFile && uid) {
        try {
          const ext = licenseFile.name.split('.').pop();
          const fileRef = storageRef(storage, `realtors/${uid}/license.${ext}`);
          await uploadBytes(fileRef, licenseFile);
          const url = await getDownloadURL(fileRef);
          await updateDoc(doc(db, 'realtors', uid), {
            license_url: url,
            business_license_url: url,
            auto_verification_status: 'ocr_processing',
            license_uploaded_at: new Date(),
          });
          // 국세청 본 검증 + OCR 필드 저장은 백그라운드
          verifyBusinessLicenseRemote({
            partnerId: uid,
            businessNumber: bizNoDigits,
            licenseUrl: url,
            ocrFields: ocrResult?.fields || null,
            ocrFailed,
            collection: 'realtors',
          }).catch((verifyErr) => console.error('자동검증 호출 실패:', verifyErr));
        } catch (uploadErr) {
          console.error('사업자등록증 업로드 실패:', uploadErr);
        }
      }

      setConsentPath('landing_signup_realtor');
      window.alert('가입이 완료되었습니다.\n이메일과 비밀번호로 로그인해 주세요.');
      window.location.assign(signupRedirectTarget);
    } catch (error) {
      const code = error?.code || '';
      // 연락처 중복 → 알림 후 로그인 페이지로 튕기기
      const isPhoneDup =
        code === 'auth/phone-already-in-use' ||
        code === 'auth/manager-phone-already-in-use' ||
        code === 'auth/phone-already-linked';
      if (isPhoneDup) {
        window.alert('이미 가입된 연락처입니다.\n로그인 후 이용해 주세요.');
        navigate('/login', { replace: true });
        return;
      }
      if (code === 'auth/email-already-in-use') setErrorMsg('이미 가입된 이메일입니다.');
      else if (code === 'auth/weak-password') setErrorMsg('비밀번호는 6자 이상이어야 합니다.');
      else setErrorMsg(error?.message || '회원가입 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  return (
    <div className="min-h-screen overflow-x-clip bg-slate-50">
      {(loading || otp.loading) && (
        <LoadingOverlay
          message={
            loading
              ? '가입 신청을 처리 중입니다.\n잠시만 기다려 주세요...'
              : '인증번호를 발송 중입니다...'
          }
        />
      )}
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-14 max-w-xl items-center px-4">
          <button
            type="button"
            onClick={() => {
              if (window.history.length > 1) navigate(-1);
              else navigate('/login', { replace: true });
            }}
            className="flex items-center gap-2 text-sm font-medium text-slate-500 transition hover:text-slate-800"
          >
            <IoChevronBack className="h-5 w-5" />
            뒤로가기
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-xl px-4 py-10 pb-40 md:pb-10">
        <div className="mb-6 rounded-2xl border border-blue-100 bg-blue-50/60 p-4">
          <p className="text-sm font-bold text-slate-900">이음 앱 다운로드</p>
          <p className="mt-1 text-xs leading-5 text-slate-500">앱으로 설치하면 고객 알림과 관리가 더 편해요.</p>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <a
              href="https://apps.apple.com/kr/app/id6767453391"
              target="_blank"
              rel="noopener noreferrer"
              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, backgroundColor: '#0f172a', color: '#fff', padding: '13px 10px', borderRadius: 12, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}
            >
              <IoLogoApple style={{ width: 16, height: 16 }} /> App Store
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=ieum.android.a"
              target="_blank"
              rel="noopener noreferrer"
              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, backgroundColor: '#2563eb', color: '#fff', padding: '13px 10px', borderRadius: 12, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}
            >
              <IoLogoGooglePlaystore style={{ width: 16, height: 16 }} /> Google Play
            </a>
          </div>
        </div>

        <div className="mb-8">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-1.5 text-xs font-semibold text-blue-700">
            공인중개사 전용
          </div>
          <h1 className="mb-2 text-2xl font-bold text-slate-900">공인중개사 회원가입</h1>
          <p className="text-sm leading-6 text-slate-500">
            가입이 완료되면 고객 초대 링크와 QR 코드, 전용 접수 폼 기능을 사용할 수 있습니다.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <section className="space-y-4">
            <SectionHeading
              title="회원 유형 선택"
              description="최초 가입 시에만 선택할 수 있으며, 이후 변경이 어렵습니다."
            />
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'personal', label: '개인' },
                { value: 'business', label: '사업자' },
              ].map((opt) => {
                const selected = userType === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setUserType(opt.value)}
                    className={`rounded-xl border px-4 py-3 text-sm font-semibold transition ${
                      selected
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </section>

          <section className="space-y-4">
            <SectionHeading
              title="사무소 정보"
              description="승인 심사에 필요한 기본 정보를 입력해 주세요."
            />
            <InputField
              icon={IoBusinessOutline}
              label="사무소명"
              required
              value={officeName}
              onChange={setOfficeName}
              placeholder="예: 이음 공인중개사 사무소"
            />
            {userType === 'business' ? (
              <>
                <InputField
                  icon={IoBusinessOutline}
                  label="사업자 등록번호"
                  required
                  value={businessNumber}
                  onChange={(v) => setBusinessNumber(formatBizNo(v))}
                  placeholder="000-00-00000"
                />
                <FieldShell label="사업자 등록증" required icon={IoDocumentTextOutline}>
                  <div className="flex items-stretch gap-2">
                    <div
                      className={`flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm ${
                        licenseFile ? 'text-slate-900' : 'text-slate-400'
                      }`}
                    >
                      {licenseFile ? (
                        <>
                          <IoDocumentTextOutline className="h-4 w-4 shrink-0" />
                          <span className="flex-1 truncate">{licenseFile.name}</span>
                          <button
                            type="button"
                            onClick={() => setLicenseFile(null)}
                            className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500"
                          >
                            <IoCloseOutline className="h-3.5 w-3.5" />
                          </button>
                        </>
                      ) : (
                        <span>PDF, JPG, PNG 10MB 이하</span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => licenseInputRef.current?.click()}
                      className="shrink-0 rounded-xl border border-blue-500 bg-white px-4 text-sm font-semibold text-blue-600"
                    >
                      파일 선택
                    </button>
                    <input
                      ref={licenseInputRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleLicenseChange}
                      className="hidden"
                    />
                  </div>
                </FieldShell>
              </>
            ) : null}
            <FieldShell label="주소" required icon={IoLocationOutline}>
              <div className="flex items-stretch gap-2">
                <input
                  type="text"
                  value={address}
                  readOnly
                  onClick={() => setAddressModal(true)}
                  placeholder="주소 검색 버튼을 눌러 주세요."
                  className="w-full cursor-pointer rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-300 focus:ring-4 focus:ring-blue-100"
                />
                <button
                  type="button"
                  onClick={() => setAddressModal(true)}
                  className="shrink-0 rounded-xl border border-blue-500 bg-white px-4 text-sm font-semibold text-blue-600"
                >
                  주소 검색
                </button>
              </div>
            </FieldShell>
            <FieldShell label="상세주소" icon={IoLocationOutline}>
              <input
                type="text"
                value={addressDetail}
                onChange={(e) => setAddressDetail(e.target.value)}
                placeholder="동·호수 등 상세주소를 입력해 주세요."
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-300 focus:ring-4 focus:ring-blue-100"
              />
            </FieldShell>
            <InputField
              icon={IoLocationOutline}
              label="활동 지역"
              value={region}
              onChange={setRegion}
              placeholder="예: 서울 강남구"
            />
          </section>

          <section className="space-y-4">
            <SectionHeading
              title="담당자 정보"
              description="고객 응대와 계정 안내에 사용될 정보입니다."
            />
            <InputField
              icon={IoPersonOutline}
              label="이름"
              required
              value={name}
              onChange={setName}
              placeholder="홍길동"
            />
            <FieldShell label="연락처" required icon={IoCallOutline}>
              <div className="flex items-stretch gap-2">
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => {
                    if (otp.verified) return;
                    setPhone(formatPhone(e.target.value));
                  }}
                  placeholder="010-0000-0000"
                  inputMode="numeric"
                  readOnly={otp.verified}
                  style={{ width: '150px', flexGrow: 0, flexShrink: 0 }}
                  className={`min-w-0 rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-blue-300 focus:ring-4 focus:ring-blue-100 ${
                    otp.verified ? 'bg-slate-50 text-slate-500' : 'bg-white text-slate-900'
                  }`}
                />
                <button
                  type="button"
                  onClick={otp.sendOtp}
                  disabled={otp.loading || phoneDigits.length < 10 || otp.verified}
                  className="shrink-0 whitespace-nowrap rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {otp.verified ? '인증완료' : otp.sent ? '재전송' : '인증번호 받기'}
                </button>
              </div>

              {otp.sent && !otp.verified && (
                <div style={{
                  marginTop: 12,
                  borderRadius: 12,
                  border: '1px solid #E2E8F0',
                  background: '#F8FAFC',
                  padding: 12,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 500, color: '#475569' }}>인증번호 6자리</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: otp.remainSec > 0 ? '#2563EB' : '#EF4444' }}>
                      {formatOtpTime(otp.remainSec)}
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 6 }}>
                    {otp.code.map((v, i) => (
                      <input
                        key={i}
                        ref={(el) => { otpInputsRef.current[i] = el; }}
                        value={v}
                        onChange={(e) => otp.setCodeAt(i, e.target.value, otpInputsRef)}
                        onKeyDown={(e) => otp.handleCodeKey(i, e, otpInputsRef)}
                        onPaste={(e) => otp.handleCodePaste(e, otpInputsRef)}
                        type="text"
                        inputMode="numeric"
                        autoComplete={i === 0 ? 'one-time-code' : 'off'}
                        maxLength={6}
                        disabled={otp.loading || otp.remainSec <= 0}
                        style={{
                          width: '100%',
                          aspectRatio: '1 / 1',
                          minWidth: 0,
                          padding: 0,
                          borderRadius: 8,
                          border: '1px solid #CBD5E1',
                          background: otp.loading || otp.remainSec <= 0 ? '#F1F5F9' : '#FFFFFF',
                          textAlign: 'center',
                          fontSize: 18,
                          fontWeight: 700,
                          color: '#0F172A',
                          outline: 'none',
                          boxSizing: 'border-box',
                        }}
                      />
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={otp.verify}
                    disabled={otp.loading || otp.codeString.length !== OTP_LENGTH || otp.remainSec <= 0}
                    style={{
                      marginTop: 12,
                      width: '100%',
                      borderRadius: 8,
                      border: 0,
                      background: '#0F172A',
                      color: '#FFFFFF',
                      padding: '10px 0',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: otp.loading || otp.codeString.length !== OTP_LENGTH || otp.remainSec <= 0 ? 'not-allowed' : 'pointer',
                      opacity: otp.loading || otp.codeString.length !== OTP_LENGTH || otp.remainSec <= 0 ? 0.5 : 1,
                    }}
                  >
                    {otp.loading ? '확인 중...' : '인증 확인'}
                  </button>
                </div>
              )}

              {otp.verified && (
                <p className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-emerald-600">
                  <IoCheckmarkCircle className="h-4 w-4" />
                  전화번호 인증이 완료되었습니다.
                </p>
              )}

              {otp.errMsg && !otp.verified && (
                <p className="mt-2 text-xs font-medium text-red-500">{otp.errMsg}</p>
              )}
            </FieldShell>
            <div>
              <FieldShell label="이메일 (로그인 ID)" required icon={IoMailOutline}>
                <EmailDomainInput
                  value={email}
                  onChange={setEmail}
                  placeholder="example"
                  inputClassName="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-300 focus:ring-4 focus:ring-blue-100"
                />
              </FieldShell>
              <p className="mt-2 text-xs text-slate-500">해당 이메일은 로그인 ID로 사용됩니다.</p>
            </div>
          </section>

          <section className="space-y-4">
            <SectionHeading
              title="비밀번호 설정"
              description="승인 후 사용할 비밀번호를 입력해 주세요."
            />
            <PasswordField
              label="비밀번호"
              value={password}
              onChange={setPassword}
              placeholder="8자 이상 입력"
            />
            <PasswordField
              label="비밀번호 확인"
              value={passwordConfirm}
              onChange={setPasswordConfirm}
              placeholder="비밀번호를 한 번 더 입력"
            />
            {passwordConfirm && password !== passwordConfirm ? (
              <p className="text-xs font-medium text-red-500">
                비밀번호가 서로 일치하지 않습니다.
              </p>
            ) : null}
          </section>

          <section className="space-y-3">
            <SectionHeading
              title="이음 공인중개사 서비스 이용 동의"
              description="모든 필수 약관에 동의해 주셔야 가입이 가능합니다."
            />
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <label className="flex cursor-pointer items-center gap-3 border-b border-slate-200 pb-3">
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={toggleAll}
                  className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-semibold text-slate-900">전체 동의합니다</span>
              </label>
              <div className="mt-3 space-y-2.5">
                {TERM_ITEMS.map((t) => (
                  <div key={t.id} className="flex items-center justify-between gap-2">
                    <label className="flex flex-1 cursor-pointer items-center gap-3">
                      <input
                        type="checkbox"
                        checked={!!terms[t.id]}
                        onChange={() => toggleTerm(t.id)}
                        className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm leading-6 text-slate-700">{t.label}</span>
                    </label>
                    <Link
                      to={`/terms/${t.id}`}
                      className="shrink-0 text-xs font-medium text-blue-600 underline-offset-2 hover:underline"
                    >
                      약관보기
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {errorMsg ? <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">{errorMsg}</p> : null}

          <div className="sticky bottom-0 left-0 right-0 -mx-6 bg-white px-6 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4 md:static md:mx-0 md:bg-transparent md:px-0 md:pb-0 md:pt-0">
            <button
              type="submit"
              disabled={!isValid || loading}
              className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-3.5 text-sm font-bold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? (
                <>
                  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  가입 처리 중...
                </>
              ) : (
                '회원가입'
              )}
            </button>
          </div>
        </form>
      </div>

      {addressModal && (
        <div
          onClick={() => setAddressModal(false)}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl"
          >
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
              <h3 className="text-base font-semibold text-slate-900">주소 검색</h3>
              <button
                type="button"
                onClick={() => setAddressModal(false)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"
              >
                <IoCloseOutline size={22} />
              </button>
            </div>
            <DaumPostcode
              onComplete={handleAddressSelect}
              style={{ width: '100%', height: 440 }}
              autoClose={false}
            />
          </div>
        </div>
      )}
    </div>
  );
}
